import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { post } from '../lib/api';
import { useAtomValue } from 'jotai';
import { selGuildAtom } from '../atoms/guild';

interface SystemCard { label: string; value: string; icon: string; color: string; }
interface GuildStat  { id: string; name: string; icon?: string; members: number; tickets: { total: number; open: number }; music: { now_playing: string | null }; }
interface DashData   { system_cards: SystemCard[]; guilds: GuildStat[]; latency: number; }

export function StatsPage() {
  const [data, setData]       = useState<DashData | null>(null);
  const [loading, setLoading] = useState(false);
  const selGuildId            = useAtomValue(selGuildAtom);

  const fetchData = async () => {
    setLoading(true);
    try {
      const d = await post<DashData>('/panel/stats');
      setData(d);
    } catch (err: unknown) {
      toast.error('Failed to load: ' + (err instanceof Error ? err.message : 'error'));
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const id = setInterval(fetchData, 15_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const guild = data?.guilds?.find(g => g.id === selGuildId) ?? data?.guilds?.[0];
  const cards = data?.system_cards ?? [];

  return (
    <div className="flex flex-col gap-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[9px] font-mono tracking-[0.2em] text-slate-600 uppercase mb-1">SC Labs · Owner</div>
          <h1 className="text-2xl font-black uppercase tracking-tight text-white">
            Dashboard <span className="text-cyberGreen neon-text-glow">Overview</span>
          </h1>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-cyberGreen/10 border border-cyberGreen/20 text-cyberGreen text-xs font-bold uppercase tracking-wider hover:bg-cyberGreen/15 transition-all disabled:opacity-50"
        >
          <i className={`fa-solid fa-rotate text-xs ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Members',  value: guild?.members?.toLocaleString()        ?? '—', icon: 'fa-solid fa-users',              color: '#00ffd1' },
          { label: 'Total Tickets',  value: guild?.tickets?.total?.toLocaleString() ?? '—', icon: 'fa-solid fa-ticket',             color: '#a78bfa' },
          { label: 'Open Tickets',   value: guild?.tickets?.open?.toLocaleString()  ?? '—', icon: 'fa-solid fa-circle-exclamation', color: '#00ff66' },
          { label: 'Bot Latency',    value: data?.latency ? Math.round(data.latency) + ' ms' : '—', icon: 'fa-solid fa-signal',    color: '#00ff66' },
        ].map(stat => (
          <div key={stat.label} className="glass-card rounded-2xl p-4 flex items-center justify-between">
            <div>
              <div className="text-[9px] font-mono text-slate-500 uppercase tracking-wider">{stat.label}</div>
              <h3
                className="font-black text-2xl text-white mt-1"
                style={stat.color === '#00ff66' ? { color: '#00ff66', textShadow: '0 0 8px #00ff66' } : {}}
              >
                {stat.value}
              </h3>
            </div>
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: stat.color + '12', border: `1px solid ${stat.color}25` }}
            >
              <i className={`${stat.icon} text-sm`} style={{ color: stat.color }} />
            </div>
          </div>
        ))}
      </div>

      {/* System Status */}
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-cyberGreen/10 border border-cyberGreen/20 flex items-center justify-center">
              <i className="fa-solid fa-terminal text-cyberGreen text-sm" />
            </div>
            <h3 className="font-black text-lg text-white">System Status</h3>
          </div>
          <span className="flex items-center gap-1.5 text-[10px] font-mono text-cyberGreen font-bold tracking-widest neon-text-glow">
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-cyberGreen mr-1" />
            NODE SECURE
          </span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.length === 0 ? (
            <div className="col-span-full text-center text-slate-600 text-xs font-mono py-6">
              {loading
                ? <><i className="fa-solid fa-spinner animate-spin mr-2 text-cyberGreen" />Loading metrics...</>
                : 'No system data available.'
              }
            </div>
          ) : cards.map(card => (
            <div
              key={card.label}
              className="rounded-xl p-3 flex items-center gap-3"
              style={{ background: card.color + '08', border: `1px solid ${card.color}18` }}
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: card.color + '15', border: `1px solid ${card.color}25` }}
              >
                <i className={`${card.icon} text-xs`} style={{ color: card.color }} />
              </div>
              <div>
                <div className="text-[9px] font-mono text-slate-500 uppercase tracking-wider">{card.label}</div>
                <div className="text-sm font-black text-white mt-0.5">{card.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Now Playing */}
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-cyberGreen/10 border border-cyberGreen/20 flex items-center justify-center">
            <i className="fa-solid fa-music text-cyberGreen text-sm" />
          </div>
          <h3 className="font-black text-lg text-white">Now Playing</h3>
        </div>
        <p className="text-sm font-mono text-slate-300 mt-1">{guild?.music?.now_playing ?? '(none)'}</p>
        <p className="text-xs font-mono text-slate-500 mt-3">
          Go to{' '}
          <a href="/panel/optimaLink" className="text-cyberGreen hover:underline font-semibold">
            OptimaLink
          </a>{' '}
          to control playback and EQ.
        </p>
      </div>
    </div>
  );
}
